import { inject } from '@angular/core';
import { HttpLink } from 'apollo-angular/http';
import { ApolloLink, split } from '@apollo/client/core';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { createClient } from 'graphql-ws';
import extractFiles from 'extract-files/extractFiles.mjs';
import isExtractableFile from 'extract-files/isExtractableFile.mjs';
import { environment } from '../../../environments/environment';
import authLink from './auth-link';
import errorLink from './error-link';
import cache from './cache';
import { WsConnectionService, WsConnectionState } from '../services/ws-connection.service';

interface Definition {
  kind: string;
  operation?: string;
}

// apollo-angular calls extractFiles(body) with one arg; wrap to supply isExtractableFile as required 2nd arg
function extractFilesWrapper(body: unknown) {
  return extractFiles(body, isExtractableFile);
}

export function apolloOptionsFactory() {
  const httpLink = inject(HttpLink);
  const wsSvc    = inject(WsConnectionService);

  let hasConnectedBefore = false;

  const wsLink = new GraphQLWsLink(
    createClient({
      url: environment.graphql_WS_URI,
      connectionParams: () => {
        let token: string | null = null;
        try { token = JSON.parse(localStorage.getItem('access_token') as string); } catch { token = null; }
        return { Authorization: token ? `Bearer ${token}` : '' };
      },
      retryAttempts: Infinity,
      keepAlive: 10000,
      on: {
        connecting: () => {
          const isReconnect = hasConnectedBefore;
          wsSvc.onConnectionStatusChange(
            isReconnect ? WsConnectionState.RECONNECTING : WsConnectionState.CONNECTING,
            isReconnect,
          );
        },
        connected: () => {
          const isReconnect = hasConnectedBefore;
          hasConnectedBefore = true;
          wsSvc.onConnectionStatusChange(WsConnectionState.CONNECTED, isReconnect);
        },
        closed: () => {
          wsSvc.onConnectionStatusChange(WsConnectionState.CLOSED, false);
        },
        error: () => {
          wsSvc.onConnectionStatusChange(WsConnectionState.ERROR, false);
        },
      },
    })
  );

  const link = split(
    ({ query }) => {
      const { kind, operation }: Definition = getMainDefinition(query);
      return kind === 'OperationDefinition' && operation === 'subscription';
    },
    wsLink,
    ApolloLink.from([
      authLink,
      errorLink,
      httpLink.create({ uri: environment.graphqlURI, extractFiles: extractFilesWrapper as never }),
    ]),
  );

  return {
    link,
    cache,
    defaultOptions: {
      watchQuery: { fetchPolicy: 'no-cache' as const, errorPolicy: 'all' as const },
      query:       { fetchPolicy: 'no-cache' as const, errorPolicy: 'all' as const },
    },
  };
}
